import { html } from 'lit';
import { sectionHeading } from '../components/sectionHeading.js';
import type { LandingContent } from '../../shared/content/landingContent.js';
import gvpMemberLogo from '../../img/rnd/GVP-Logo_Mitglied_quer_weiss_RGB_2 1.png';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import './partnerMatrixElement.js';

export const partnersSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => {
  const items = content.partners.items ?? [];

  return html`
    <section
      id="partner"
      class=${editorNodeClass(editor, 'section.partners', 'vp-section vp-partners-section')}
      style=${editorNodeStyle(editor, 'section.partners')}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'section.partners', event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, 'section.partners', event)}
    >
      <div class="vp-shell">
        ${sectionHeading({
          eyebrow: content.partners.eyebrow,
          title: content.partners.title,
          highlight: content.partners.titleHighlight,
          text: content.partners.text,
          align: 'center',
          editor,
          elementPrefix: 'partners',
        })}

        <vp-partner-matrix class="vp-partners">
          <div class="vp-partners-copy">
            <div class="vp-partners-active">
              ${items.map((item, index) => {
                const descId = `partners.item.${index}`;
                return html`
                  <div class="vp-partner-desc ${index === 0 ? 'is-active' : ''}" data-index=${index}>
                    <strong
                      class=${editorNodeClass(editor, `${descId}.name`, 'vp-partner-active-name')}
                      style=${editorNodeStyle(editor, `${descId}.name`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${descId}.name`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${descId}.name`, event)}
                      >${item.name}</strong
                    >
                    <p
                      class=${editorNodeClass(editor, `${descId}.description`, 'vp-partner-active-text')}
                      style=${editorNodeStyle(editor, `${descId}.description`)}
                      @pointerdown=${(event: PointerEvent) =>
                        editorNodePointerDown(editor, `${descId}.description`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${descId}.description`, event)}
                    >
                      ${item.description}
                    </p>
                    ${item.href
                      ? html`<a
                          class=${editorNodeClass(editor, `${descId}.href`, 'vp-partner-active-link')}
                          style=${editorNodeStyle(editor, `${descId}.href`)}
                          href=${item.href}
                          @pointerdown=${(event: PointerEvent) =>
                            editorNodePointerDown(editor, `${descId}.href`, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${descId}.href`, event)}
                          >${item.name}
                          <span class="icon-[material-symbols--arrow-forward]"></span>
                        </a>`
                      : ''}
                  </div>
                `;
              })}
            </div>
            ${items.length > 1
              ? html`<div class="vp-partners-progress" role="presentation">
                  ${items.map(
                    (item, index) => html`
                      <button
                        type="button"
                        class="vp-partner-progress-seg ${index === 0 ? 'is-active' : ''}"
                        data-index=${index}
                        aria-label=${item.name}
                      >
                        <span class="vp-partner-progress-fill"></span>
                      </button>
                    `,
                  )}
                </div>`
              : ''}
            ${content.partners.trustLabel
              ? html`<p
                  class=${editorNodeClass(editor, 'partners.trustLabel', 'vp-partners-trust')}
                  style=${editorNodeStyle(editor, 'partners.trustLabel')}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'partners.trustLabel', event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, 'partners.trustLabel', event)}
                >
                  <img
                    class="vp-partners-trust-logo"
                    src=${gvpMemberLogo}
                    alt=""
                    width="611"
                    height="300"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>${content.partners.trustLabel}</span>
                </p>`
              : ''}
          </div>

          <div
            class=${editorNodeClass(editor, 'partners.items', 'vp-partners-matrix')}
            style=${editorNodeStyle(editor, 'partners.items')}
            role="tablist"
            aria-label=${content.locale === 'tr' ? 'İş ortakları' : content.locale === 'en' ? 'Partners' : 'Partner'}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'partners.items', event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, 'partners.items', event)}
          >
            ${items.map((item, index) => {
              const cellId = `partners.item.${index}`;
              return html`
                <button
                  type="button"
                  class=${editorNodeClass(editor, cellId, `vp-partner-logo ${index === 0 ? 'is-active' : ''}`)}
                  style=${editorNodeStyle(editor, cellId)}
                  data-index=${index}
                  aria-pressed=${index === 0 ? 'true' : 'false'}
                  aria-label=${item.name}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, cellId, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, cellId, event)}
                >
                  <img
                    class=${editorNodeClass(editor, `${cellId}.logo`, 'vp-partner-logo-img')}
                    style=${editorNodeStyle(editor, `${cellId}.logo`)}
                    src=${item.logo}
                    alt=${item.alt}
                    loading="lazy"
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cellId}.logo`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${cellId}.logo`, event)}
                  />
                </button>
              `;
            })}
          </div>
        </vp-partner-matrix>
      </div>
    </section>
  `;
};
